import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';

import {
  checkModuleSelection,
  getModuleCatalog,
  getModulePresets,
  type ModuleDefinition,
  type ModulePreset,
  type ModuleSelectionCheck,
} from '../api/tenant-modules.graphql';

/** El catálogo y los presets cambian solo con un despliegue: se cachean durante la sesión. */
const CATALOG_STALE_MS = 30 * 60 * 1000;

export function useModuleCatalog(): UseQueryResult<ModuleDefinition[]> {
  return useQuery({
    queryKey: ['platform-module-catalog'],
    queryFn: getModuleCatalog,
    staleTime: CATALOG_STALE_MS,
  });
}

export function useModulePresets(): UseQueryResult<ModulePreset[]> {
  return useQuery({
    queryKey: ['platform-module-presets'],
    queryFn: getModulePresets,
    staleTime: CATALOG_STALE_MS,
  });
}

/**
 * OS-1716 — valida una selección con las reglas del API (sin escribir). La respuesta solo depende
 * de la selección, así que se cachea por selección ordenada.
 */
export function useModuleSelectionCheck(
  modules: readonly string[],
  enabled = true,
): UseQueryResult<ModuleSelectionCheck> {
  const sorted = [...modules].sort();
  return useQuery({
    queryKey: ['platform-module-selection-check', sorted],
    queryFn: () => checkModuleSelection(sorted),
    enabled,
    staleTime: CATALOG_STALE_MS,
    placeholderData: keepPreviousData,
  });
}

/** Estado de la validación para bloquear el avance mientras no haya una respuesta vigente. */
export type SelectionCheckState =
  | { state: 'checking' }
  | { state: 'valid' }
  | { state: 'invalid'; message: string }
  | { state: 'error'; message: string };

export function selectionCheckState(query: UseQueryResult<ModuleSelectionCheck>): SelectionCheckState {
  if (query.isError) {
    return { state: 'error', message: 'No se pudo validar la selección de módulos. Intenta de nuevo.' };
  }
  if (!query.data || query.isPlaceholderData) {
    return { state: 'checking' };
  }
  return query.data.message
    ? { state: 'invalid', message: query.data.message }
    : { state: 'valid' };
}
